import * as React from 'react';

import ConsumerSimpleDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/ConsumerSimpleDesktopMainNav';
import ConsumerSimpleMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/ConsumerSimpleMobileMainNav';
import DefaultConsumerPageHeaderProvider from 'bundles/composable-page-header-consumer/providers/DefaultConsumerPageHeaderProvider';
import GlobalPageHeaderContainer from 'bundles/composable-page-header/components/GlobalPageHeaderContainer';
import GlobalPageHeaderStateProvider from 'bundles/composable-page-header/providers/GlobalPageHeaderStateProvider';

type Props = {
  // An array of custom header logos, for when we want to override the default Coursera logo.
  // By default, this is undefined, so the Coursera home logo is used. It turns black when the logo link is disabled.
  customHeaderLogos?: Array<React.ReactNode>;
  // Makes the Coursera logo clickable or not. By default, it is clickable.
  enableCourseraLogoLink?: boolean;
  // Allows the learner to switch between various Enterprise or Degree programs. By default, this is disabled.
  showProgramSwitcher?: boolean;
  // Shows/hides the shopping cart in the header. By default, this is enabled.
  showShoppingCart?: boolean;
  // Allows a logged-in learner to quickly change the language setting for the Coursera platform. By default, this is enabled.
  showLanguageDropdown?: boolean;
  // Allows us to show or hide Coach in the top banner, currently visible to Courserians only. By default, this is enabled.
  showCoach?: boolean;
  // Shows the account dropdown, making account controls visible right in the header. By default, this is enabled.
  showAcctDropdown?: boolean;
  // Instead of requiring a tap to see account items on mobile, this lets the learner see all account dropdown items
  // right in the main menu instead. By default, this is disabled.
  showAcctItemsInMain?: boolean;
  // Only shows the logout option in the learner's account settings on desktop and mobile, and supports both the expanded
  // mobile menu as well as the account submenu. By default, this is disabled.
  acctDropdownLogoutOnly?: boolean;
  // Shows the signup button for logged-out learners. Enabled by default.
  showSignupBtn?: boolean;
  // Shows the login button for logged-out learners. Enabled by default.
  showLoginBtn?: boolean;
  // Shows the mobile hamburger menu. Enabled by default.
  showMobileHamburger?: boolean;
};

const SimplifiedHeader = ({
  customHeaderLogos,
  acctDropdownLogoutOnly,
  enableCourseraLogoLink,
  showProgramSwitcher,
  showShoppingCart,
  showLanguageDropdown,
  showCoach,
  showAcctDropdown,
  showAcctItemsInMain,
  showSignupBtn,
  showLoginBtn,
  showMobileHamburger,
}: Props) => {
  return (
    <GlobalPageHeaderContainer
      desktopMainNav={
        <ConsumerSimpleDesktopMainNav
          customHeaderLogos={customHeaderLogos}
          acctDropdownLogoutOnly={acctDropdownLogoutOnly}
          enableCourseraLogoLink={enableCourseraLogoLink}
          showProgramSwitcher={showProgramSwitcher}
          showShoppingCart={showShoppingCart}
          showLanguageDropdown={showLanguageDropdown}
          showCoach={showCoach}
          showAcctDropdown={showAcctDropdown}
          showSignupBtn={showSignupBtn}
          showLoginBtn={showLoginBtn}
        />
      }
      mobileMainNav={
        <ConsumerSimpleMobileMainNav
          customHeaderLogos={customHeaderLogos}
          acctDropdownLogoutOnly={acctDropdownLogoutOnly}
          enableCourseraLogoLink={enableCourseraLogoLink}
          showProgramSwitcher={showProgramSwitcher}
          showLanguageDropdown={showLanguageDropdown}
          showAcctDropdown={showAcctDropdown}
          showAcctItemsInMain={showAcctItemsInMain}
          showSignupBtn={showSignupBtn}
          showLoginBtn={showLoginBtn}
          showMobileHamburger={showMobileHamburger}
        />
      }
    />
  );
};

/**
 * This is the simplified header in the Composable Header pattern, best used for when we want learners to focus more
 * on a page's content instead of the header.
 *
 * Note: Do not make direct changes to this header unless explicitly necessary! If you need  more functionality
 * beyond what this header provides, it is encouraged to make a separate header renderer instead.
 */
const SimplifiedHeaderWithProvider = ({
  customHeaderLogos = undefined,
  enableCourseraLogoLink = true,
  showProgramSwitcher = false,
  showShoppingCart = true,
  showLanguageDropdown = true,
  showCoach = true,
  acctDropdownLogoutOnly = false,
  showAcctDropdown = true,
  showAcctItemsInMain = false,
  showSignupBtn = true,
  showLoginBtn = true,
  showMobileHamburger = true,
}: Props) => {
  return (
    <GlobalPageHeaderStateProvider>
      <DefaultConsumerPageHeaderProvider>
        <SimplifiedHeader
          customHeaderLogos={customHeaderLogos}
          enableCourseraLogoLink={enableCourseraLogoLink}
          showProgramSwitcher={showProgramSwitcher}
          showShoppingCart={showShoppingCart}
          showLanguageDropdown={showLanguageDropdown}
          showCoach={showCoach}
          acctDropdownLogoutOnly={acctDropdownLogoutOnly}
          showAcctDropdown={showAcctDropdown}
          showAcctItemsInMain={showAcctItemsInMain}
          showSignupBtn={showSignupBtn}
          showLoginBtn={showLoginBtn}
          showMobileHamburger={showMobileHamburger}
        />
      </DefaultConsumerPageHeaderProvider>
    </GlobalPageHeaderStateProvider>
  );
};

export default SimplifiedHeaderWithProvider;
